import { useEffect, useState } from "react";
import { AnyDefinition, DatabaseNode, InferType } from "@lifeat/shared/data-models/definition";
import { useDatabaseContext } from "@contexts/DatabaseContext";

type Options<
    TDefinition extends AnyDefinition,
    TPath extends string,
    TValue extends InferType<TDefinition> | null | undefined,
> = {
    /** Database node to listen to. */
    node: DatabaseNode<TDefinition, TPath>;
    /** Initial local state value before database value becomes available. */
    initialValue?: TValue;
};

/**
 * Create mutable local state that is synced with database.
 */
export function useDbWithLocalCache<
    TDefinition extends AnyDefinition,
    TPath extends string,
    TValue extends InferType<TDefinition> | null | undefined,
>(options: Options<TDefinition, TPath, TValue>) {
    const db = useDatabaseContext();
    const [value, setValue] = useState<TValue>(options.initialValue);
    useEffect(() => {
        const unsub = db.subscribe(options.node, "value", (payload) => {
            if (payload.success) {
                setValue(payload.value);
            }
        });

        return () => unsub();
    }, [db, options.node.path]);

    const changeValue = async (next: InferType<TDefinition>) => {
        setValue(next);
        await db.set(options.node, next);
    };

    return [value, changeValue] as const;
}
